import { CommonModule } from '@angular/common';
import { Component, computed, Inject, signal, WritableSignal, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SettingsService } from '@app/_services/settings.service';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { AlertService } from '@app/_services/alert.service';

export interface ImportProgressData {
  fileName: string;
  fileSize: number;
  onCancel?: () => void;
}

@Component({
  selector: 'app-import-progress-dialog',
  imports: [
    CommonModule,
    SvgComponent
  ],
  templateUrl: './import-progress-dialog.component.html',
  styleUrl: './import-progress-dialog.component.scss'
})
export class ImportProgressDialogComponent implements OnInit, OnDestroy {

  // File info as signals for full reactivity
  fileName: WritableSignal<string>;
  fileSize: WritableSignal<number>;
  private onCancel?: () => void;

  // Progress tracking (reactive)
  uploadProgress: WritableSignal<number> = signal(0);
  idleTime: WritableSignal<number> = signal(0);

  // Timer reference
  private timer?: any;
  private readonly IDLE_TIMEOUT_SECONDS = 60;
  private readonly OFFLINE_MESSAGE_THRESHOLD_SECONDS = 10;

  // Computed signals - all reactive
  statusIcon = computed(() => 'upload');

  statusColor = computed(() => this.ss.theme().color35);

  showProgressBar = computed(() => true);

  // Progress percentage display
  progressPercentage = computed(() => `${this.uploadProgress()}%`);

  // Formatted file size for display
  formattedFileSize = computed(() => {
    const bytes = this.fileSize();
    if (bytes === 0) return '0 B';

    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const size = bytes / Math.pow(k, i);

    return `${size.toFixed(i > 0 ? 1 : 0)} ${sizes[i]}`;
  });

  // Show offline message after 10 seconds of idle time (reactive)
  showOfflineMessage = computed(() => {
    return this.idleTime() >= this.OFFLINE_MESSAGE_THRESHOLD_SECONDS;
  });

  // Status message based on idle time (reactive)
  statusMessage = computed(() => {
    return this.showOfflineMessage()
      ? this.ss.t('dataUploadingProcess_offlineInfo')
      : this.ss.t('dataUploadingProcess_uploadingContent');
  });

  constructor(
    public dialogRef: MatDialogRef<ImportProgressDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ImportProgressData,
    public ss: SettingsService,
    private alertService: AlertService
  ) {
    // Initialize signals from dialog data
    this.fileName = signal(data.fileName);
    this.fileSize = signal(data.fileSize);
    this.onCancel = data.onCancel;
  }

  ngOnInit(): void {
    // Start idle timeout timer (matches Flutter's ImportProgressWidget logic)
    this.timer = setInterval(() => {
      this.idleTime.update(val => val + 1);

      if (this.idleTime() > this.IDLE_TIMEOUT_SECONDS) {
        // Auto-close after 60 seconds of no progress
        this.cleanup();
        this.dialogRef.close({ cancelled: true, timeout: true });
        this.showTimeoutDialog();
      }
    }, 1000);
  }

  /**
   * Update progress percentage (0-100)
   * Resets idle timer on each progress update (matches Flutter behavior)
   */
  updateProgress(progress: number): void {
    this.uploadProgress.set(Math.min(100, Math.max(0, progress)));

    // Reset idle timer when progress is made
    this.idleTime.set(0);
  }

  /**
   * Cancel upload and close dialog
   */
  cancel(): void {
    this.cleanup();
    if (this.onCancel) {
      this.onCancel();
    }
    this.dialogRef.close({ cancelled: true });
  }

  /**
   * Show timeout dialog (matches Flutter's showTimeoutDialog)
   */
  private async showTimeoutDialog(): Promise<void> {
    await this.alertService.alert(
      this.ss.t('importTimeout_title'),
      this.ss.t('importTimeout_content'),
      this.ss.t('common_ok')
    );
  }

  /**
   * Cleanup timer resources
   */
  private cleanup(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = undefined;
    }
  }

  ngOnDestroy(): void {
    this.cleanup();
  }
}
